/**
 * 25. Sep 2026 · Kunden-„Neuigkeiten"-Zähler (einheitliche Quelle).
 *
 * Pollt `GET /api/me/notifications` und liefert den vollständigen `total`
 * (Wochenberichte + Kostenvoranschläge + Nachrichten + Rechnungen + Dokumente
 * + beantwortete Anfragen). Speist die goldenen In-App-Abzeichen (Glocke +
 * untere Navigationsleiste) und setzt zusätzlich — als Progressive Enhancement
 * — das Abzeichen am App-Symbol über die Badging API (nur installierte PWA;
 * Farbe gibt das OS vor). Aktualisiert beim Start, im 30-s-Takt, beim
 * Zurückkehren in den Vordergrund und auf die bekannten Refresh-Events.
 */
import { useEffect, useState } from "react";
import api from "./api";

const REFRESH_EVENTS = [
  "weissblom:unread-refresh",
  "weissblom:unread-updates-refresh",
  "weissblom:unread-quotes-refresh",
];

function applyAppBadge(total) {
  try {
    if (typeof navigator === "undefined") return;
    const n = Number(total || 0);
    if ("setAppBadge" in navigator) {
      if (n > 0) navigator.setAppBadge(n).catch(() => {});
      else if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    }
  } catch {
    /* Badging API optional — App läuft normal weiter */
  }
}

export function useCustomerNotifications(enabled) {
  const [data, setData] = useState({
    total: 0, updates: 0, quotes: 0, messages: 0, invoices: 0, documents: 0, requests: 0,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    const load = async () => {
      try {
        const { data: d } = await api.get("/me/notifications");
        if (stopped) return;
        setData(d || {});
        applyAppBadge(d?.total);
      } catch {
        /* still — In-App-Abzeichen bleibt beim letzten Stand */
      }
    };
    load();
    const iv = setInterval(load, 30000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    REFRESH_EVENTS.forEach((e) => window.addEventListener(e, load));
    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      REFRESH_EVENTS.forEach((e) => window.removeEventListener(e, load));
    };
  }, [enabled]);

  return data;
}
